import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { isMuted, onMuteChange, sfxClick, toggleMute, unlockAudio, unlockAudioSync } from '../gameslib/audio'

const GAMES = [
  { path: '/game/color', label: 'color' },
  { path: '/game/sound', label: 'sound' },
  { path: '/game/time', label: 'time' },
  { path: '/game/shape', label: 'shape' },
  { path: '/game/puzzle', label: 'puzzle' },
]

export function TopNav({ modeLabel = 'solo', rounds = 5 }) {
  const { pathname } = useLocation()
  const [muted, setMutedState] = useState(() => isMuted())

  useEffect(() => onMuteChange(setMutedState), [])

  return (
    <header className="top-nav">
      <Link to="/" className="top-nav__brand" onClick={() => void sfxClick()}>
        &nbsp;<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#001E3A"><path d="M400-80 0-480l400-400 71 71-329 329 329 329-71 71Z"/></svg>
      </Link>
      <nav className="top-nav__tabs" aria-label="Games">
        {GAMES.map((g) => (
          <Link
            key={g.path}
            to={g.path}
            className={pathname === g.path ? 'top-nav__tab is-active' : 'top-nav__tab'}
            onClick={() => void sfxClick()}
          >
            {g.label}
          </Link>
        ))}
      </nav>
      <div className="top-nav__right">
        <span>{modeLabel}</span>
        <span>
          {rounds} round{rounds === 1 ? '' : 's'}
        </span>
        <div
          type="button"
          className="mute-btn"
          aria-label={muted ? 'Unmute' : 'Mute'}
          onClick={() => {
            void unlockAudio()
            setMutedState(toggleMute())
          }}
        >
          {muted ? <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#001E3A"><path d="M792-56 671-177q-25 16-53 27.5T560-131v-82q14-5 27.5-10t25.5-12L480-368v208L280-360H120v-240h128L56-792l56-56 736 736-56 56Zm-8-232-58-58q17-31 25.5-65t8.5-70q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 53-14.5 102T784-288ZM650-422l-90-90v-130q47 22 73.5 66t26.5 96q0 15-2.5 29.5T650-422ZM480-592 376-696l104-104v208Z"/></svg> : <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#001E3A"><path d="M560-131v-82q90-26 145-100t55-168q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 127-78 224.5T560-131ZM120-360v-240h160l200-200v640L280-360H120Zm440 40v-322q47 22 73.5 66t26.5 96q0 51-26.5 94.5T560-320Z"/></svg>}
        </div>
      </div>
    </header>
  )
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M8 5.5v13l11-6.5L8 5.5Z" fill="currentColor" />
    </svg>
  )
}

function MultiIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="16" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M3.5 19c.7-2.6 2.8-4 5.5-4s4.8 1.4 5.5 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M14 15.2c1.7.2 3.2 1.2 3.9 3.3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

const DIFFS = ['easy', 'hard', 'brutal']
export const ROUND_OPTIONS = [3, 5, 7, 10]

export function IntroScreen({
  title,
  blurb,
  onSolo,
  onMulti,
  difficulty = 'easy',
  onDifficulty,
  rounds = 5,
  onRounds,
  onTeach,
}) {
  const paragraphs = blurb.split('\n').filter(Boolean)
  const cycleDiff = () => {
    if (!onDifficulty) return
    void sfxClick()
    const i = DIFFS.indexOf(difficulty)
    onDifficulty(DIFFS[(i + 1) % DIFFS.length])
  }

  return (
    <div className="intro">
      {onTeach && (
        <button
          type="button"
          className="howto-trigger"
          aria-label="Show me how to play"
          title="Show me how"
          onClick={() => {
            unlockAudioSync()
            void sfxClick()
            onTeach()
          }}
        >
          ?
        </button>
      )}
      <div className="intro__top">
        <h1 className="intro__title">{title}</h1>
        <div className="intro__body">
          {paragraphs.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      </div>

      <div className="intro__footer">
        <p className="intro__mode-label">Solo or multiplayer?</p>
        <div className="intro__controls">
          <button
            type="button"
            className="circle-btn"
            aria-label="Play solo"
            title="Solo"
            onClick={() => {
              unlockAudioSync()
              void unlockAudio().then(() => sfxClick())
              onSolo()
            }}
          >
            <PlayIcon />
          </button>
          <button
            type="button"
            className="circle-btn"
            aria-label="Multiplayer"
            title="Multiplayer"
            onClick={() => {
              unlockAudioSync()
              void unlockAudio().then(() => sfxClick())
              onMulti?.()
            }}
          >
            <MultiIcon />
          </button>
          <button type="button" className="diff-toggle" aria-label={`Difficulty: ${difficulty}`} onClick={cycleDiff}>
            <span className="diff-toggle__track" data-mode={difficulty}>
              <span className="diff-toggle__knob" />
            </span>
            <span style={{ textTransform: 'capitalize' }}>{difficulty}</span>
          </button>
        </div>
        {onTeach && (
          <button
            type="button"
            className="intro__teach"
            onClick={() => {
              unlockAudioSync()
              void sfxClick()
              onTeach()
            }}
          >
            Show me how
          </button>
        )}
        {onRounds && (
          <div className="intro__rounds">
            <span className="intro__rounds-label">Rounds per player</span>
            <div className="intro__rounds-chips">
              {ROUND_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={rounds === n ? 'mp-chip is-active' : 'mp-chip'}
                  onClick={() => {
                    void sfxClick()
                    onRounds(n)
                  }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function GameShell({ children, style, className = '' }) {
  return (
    <div className={`game-shell ${className}`.trim()} style={style}>
      {children}
    </div>
  )
}

export function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5 12h12M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function PlayerBanner({ name }) {
  if (!name) return null
  return <div className="player-banner">{name}</div>
}
